import { isApiError } from '@multaqa/api-client'
import { useTranslation } from '@multaqa/i18n'
import { Button } from '@multaqa/ui/components/ui/button'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@multaqa/ui/components/ui/dialog'
import { Switch } from '@multaqa/ui/components/ui/switch'
import { useState } from 'react'
import {
  useSaveWorkflow,
  useWorkflowDocumentTypes,
  type WorkflowDefinition,
  type WorkflowInput,
} from './api'
import { WorkflowDocumentFields } from './workflow-document-fields'
import {
  delayIsValid,
  emptyStep,
  isChosen,
  stepFromResource,
  stepsToInput,
  type WorkflowMode,
  type WorkflowStepDraft,
} from './workflow-levels'
import { WorkflowModeField } from './workflow-mode-field'
import { WorkflowStepsEditor } from './workflow-steps-editor'
import { WorkflowSummary } from './workflow-summary'

type Draft = {
  document_type: string
  enabled: boolean
  mode: WorkflowMode
  threshold_enabled: boolean
  threshold_amount: string
  steps: WorkflowStepDraft[]
}
type WorkflowField = 'document_type' | 'threshold_amount'
type FieldErrors = Partial<Record<WorkflowField, string>>

const WORKFLOW_FIELDS: WorkflowField[] = ['document_type', 'threshold_amount']

/**
 * The recommended path a new escalation path starts from (ADR-0034, DP1):
 * direct manager for 48 hours, then the HR officer role, then the owner.
 */
export function defaultLadder(): WorkflowStepDraft[] {
  return [
    { ...emptyStep('level-1'), approver_type: 'direct_manager', escalate_after_hours: '48' },
    { ...emptyStep('level-2'), approver_type: 'system_role', approver_system_role: 'hr_officer' },
    {
      ...emptyStep('level-3'),
      approver_type: 'system_role',
      approver_system_role: 'company_owner',
    },
  ]
}

function newDraft(documentType: string | undefined, mode: WorkflowMode): Draft {
  return {
    document_type: documentType ?? '',
    enabled: mode === 'escalation',
    mode,
    threshold_enabled: false,
    threshold_amount: '',
    steps: mode === 'escalation' ? defaultLadder() : [emptyStep('new-step')],
  }
}

export function WorkflowDialog({
  companyId,
  editing,
  open,
  onOpenChange,
  module,
  documentType,
}: {
  companyId: string
  editing: WorkflowDefinition | null
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Limits the document picker to one module's types (its settings screen). */
  module?: string
  /** Opens a new path for this type, starting from the recommended ladder. */
  documentType?: string
}) {
  const { t } = useTranslation('workflow')
  const [draft, setDraft] = useState<Draft>(() =>
    editing
      ? {
          document_type: editing.document_type,
          enabled: editing.enabled,
          mode: editing.mode as WorkflowMode,
          threshold_enabled: Boolean(editing.threshold_amount),
          threshold_amount: editing.threshold_amount ?? '',
          steps: editing.steps.map(stepFromResource),
        }
      : newDraft(documentType, documentType ? 'escalation' : 'chain'),
  )
  const save = useSaveWorkflow(companyId, editing?.id)
  const documentTypes = useWorkflowDocumentTypes(companyId, module)
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({})
  const [stepError, setStepError] = useState<string | null>(null)
  const validationErrors =
    isApiError(save.error) && save.error.kind === 'validation' ? (save.error.errors ?? {}) : null
  const serverFieldErrors: FieldErrors = {
    document_type: validationErrors?.document_type?.[0],
    threshold_amount: validationErrors?.threshold_amount?.[0],
  }
  const fieldError = (field: WorkflowField) => fieldErrors[field] ?? serverFieldErrors[field]
  const hasMappedServerError = Object.values(serverFieldErrors).some(Boolean)
  const hasUnmappedServerError =
    validationErrors &&
    Object.keys(validationErrors).some((field) => !WORKFLOW_FIELDS.includes(field as WorkflowField))
  const generalError =
    save.error && (!validationErrors || !hasMappedServerError || hasUnmappedServerError)
      ? t('state.saveError')
      : null

  const update = (values: Partial<Draft>) => {
    save.reset()
    setFieldErrors((current) => {
      const next = { ...current }
      if ('document_type' in values) delete next.document_type
      if ('threshold_amount' in values) delete next.threshold_amount
      return next
    })
    setDraft((current) => ({ ...current, ...values }))
    if ('steps' in values) setStepError(null)
  }

  const submit = () => {
    if (save.isPending) return
    save.reset()
    const errors: FieldErrors = {}
    const documentType = draft.document_type.trim()
    const amount = draft.threshold_enabled ? draft.threshold_amount.trim() : ''

    if (!documentTypes.data?.data.some((document) => document.key === documentType)) {
      errors.document_type = t('form.documentTypeRequired')
    }
    if (draft.threshold_enabled && !amount) errors.threshold_amount = t('form.thresholdRequired')
    const stepValidation =
      draft.steps.length === 0
        ? t('form.stepsRequired')
        : draft.steps.some((step) => !isChosen(step))
          ? t('form.approverRequired')
          : draft.mode === 'escalation' &&
              draft.steps.some((step) => !delayIsValid(step.escalate_after_hours))
            ? t('levels.delayInvalid')
            : null
    setStepError(stepValidation)
    setFieldErrors(errors)
    if (Object.keys(errors).length > 0 || stepValidation) return

    const input: WorkflowInput = {
      document_type: documentType,
      enabled: draft.enabled,
      mode: draft.mode,
      threshold_amount: amount || null,
      threshold_currency: amount ? (documentTypes.data?.base_currency ?? null) : null,
      steps: stepsToInput(draft.steps, draft.mode),
    } as unknown as WorkflowInput
    save.mutate(input, { onSuccess: () => onOpenChange(false) })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        closeLabel={t('action.cancel')}
        className="flex max-h-[90dvh] max-w-2xl flex-col overflow-hidden"
      >
        <DialogHeader>
          <DialogTitle>
            {module
              ? editing
                ? t('form.pathEditTitle')
                : t('form.pathCreateTitle')
              : editing
                ? t('form.editTitle')
                : t('form.createTitle')}
          </DialogTitle>
          <DialogDescription>
            {draft.mode === 'escalation' ? t('form.escalationDescription') : t('form.description')}
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="min-h-0 flex-1 space-y-4 overflow-y-auto">
          {documentTypes.data ? (
            <WorkflowDocumentFields
              documents={documentTypes.data.data}
              baseCurrency={documentTypes.data.base_currency}
              documentType={draft.document_type}
              thresholdAmount={draft.threshold_amount}
              thresholdEnabled={draft.threshold_enabled}
              fieldError={fieldError}
              onDocumentType={(document) =>
                update({
                  document_type: document?.key ?? '',
                  ...(!document?.has_amount && {
                    threshold_enabled: false,
                    threshold_amount: '',
                  }),
                })
              }
              onThreshold={(threshold_amount) => update({ threshold_amount })}
              onThresholdEnabled={(threshold_enabled) =>
                update({ threshold_enabled, ...(!threshold_enabled && { threshold_amount: '' }) })
              }
            />
          ) : (
            <div className="grid gap-2 text-small text-muted-foreground" role="status">
              {documentTypes.isError ? t('state.optionsError') : t('state.loading')}
              {documentTypes.isError ? (
                <Button variant="outline" size="sm" onClick={() => void documentTypes.refetch()}>
                  {t('action.retry')}
                </Button>
              ) : null}
            </div>
          )}
          <label className="flex items-start gap-2 text-small" htmlFor="workflow-enabled">
            <Switch
              id="workflow-enabled"
              checked={draft.enabled}
              onCheckedChange={(enabled) => update({ enabled })}
            />
            <span className="grid gap-0.5">
              <span>{t('form.enabled')}</span>
              <span className="text-muted-foreground">{t('form.enabledDescription')}</span>
            </span>
          </label>
          <WorkflowModeField mode={draft.mode} onChange={(mode) => update({ mode })} />
          <WorkflowStepsEditor
            companyId={companyId}
            steps={draft.steps}
            mode={draft.mode}
            onChange={(steps) => update({ steps })}
            error={stepError}
          />
          <WorkflowSummary
            companyId={companyId}
            document={documentTypes.data?.data.find((type) => type.key === draft.document_type)}
            amount={draft.threshold_enabled ? draft.threshold_amount : ''}
            currency={documentTypes.data?.base_currency ?? ''}
            steps={draft.steps}
            mode={draft.mode}
          />
          {generalError ? (
            <p className="text-small text-danger" role="alert">
              {generalError}
            </p>
          ) : null}
        </DialogBody>
        <DialogFooter showCloseButton={false} closeLabel={t('action.cancel')}>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('action.cancel')}
          </Button>
          <Button loading={save.isPending} onClick={submit}>
            {t('action.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
